import currencyFormatter from 'currency-formatter'

const PRIMARY_CURRENCIES = ['USD', 'GBP', 'EUR']

const PRIMARY_SYMBOLS = ['$', '£', '€']

const DELIMITERS = { thousand: ',', decimal: '.' }

type FormatOptions = {
  code?: string,
  locale?: string,
  symbol?: string,
  decimal?: string,
  thousand?: string,
  precision?: number,
  format?: string | {
    pos: string,
    neg: string,
    zero: string
  }
}

function findCurrency(currency: string) {
  if(currency === 'CUD') {
    return {
      code: "CUD",
      symbol: "CUD",
      thousandsSeparator: ",",
      decimalSeparator: "",
      symbolOnLeft: true,
      spaceBetweenAmountAndSymbol: false,
      decimalDigits: 0,
      precision: 0
    }
  }

  const cur = currencyFormatter.findCurrency(currency) || {}
  if (PRIMARY_SYMBOLS.includes(cur.symbol) && !PRIMARY_CURRENCIES.includes(currency)) {
    return undefined
  }
  return cur
}

export function formatCurrency(value: number, currency: string, locale?: string, options?: FormatOptions): string {
  const cur = findCurrency(currency)
  return currencyFormatter.format(value, {
    ...cur,
    code: currency,
    format: cur ? '%s %v' : `%v ${currency}`,
    ...DELIMITERS,
    ...options
  })
}

export function formatCurrencyDifference(value: number, currency: string, locale?: string): string {
  const cur = findCurrency(currency)
  const valueFormat = value >= 0 ? '+%v' : '%v'
  return currencyFormatter.format(value, {
    ...cur,
    code: currency,
    format: cur ? `%s ${valueFormat}` : `${valueFormat} ${currency}`,
    ...DELIMITERS,
  })
}

export function formatProfitLoss(value: number, currency: string, locale?: string, options?: FormatOptions): string {
  const cur = findCurrency(currency)
  const format = value > 0 ? '%s +%v' : '%s %v'
  return currencyFormatter.format(value, { code: currency, format, thousand: ',', decimal: '.', ...cur, ...options })
}

export function formatPrice(
  value: number,
  precision: number,
  currency: string,
  locale?: string
): string {
  const cur = findCurrency(currency)
  return currencyFormatter.format(value, {
    code: currency,
    precision,
    format: cur ? '%s %v' : `%v ${currency}`,
    ...DELIMITERS
  })
}

export function formatPriceDifference(
  value: number,
  precision: number,
  currency: string,
  positive = true
): string {
  const cur = findCurrency(currency)
  const valueFormat = positive ? '+%v' : '%v'
  return currencyFormatter.format(value, {
    code: currency,
    precision,
    format: cur ? `%s ${valueFormat}` : `${valueFormat} ${currency}`,
    ...DELIMITERS
  })
}

export function currencySymbol(currency: string): string {
  return findCurrency(currency)?.symbol || currency
}

export function formatDelta(value: number, precision: number): string {
  const format = value > 0 ? '+%v' : '%v'
  return currencyFormatter.format(value, { precision, format, ...DELIMITERS })
}

export function formatNumber(value: number, precision: number, currency?: string): string {
  return currencyFormatter.format(value, (currency === 'CUD') ? { precision: 0 } : { precision, ...DELIMITERS })
}

export function unformatNumber(value: string, precision: number): number {
  return currencyFormatter.unformat(value, { precision, ...DELIMITERS })
}
